import { generatePath, useHistory, useParams } from 'react-router-dom';

import { FC } from 'react';
import { IdParamsType } from 'utils/types';
import { PoolForm } from './PoolForm/PoolForm';
import { PunchupCardTileGrid } from 'features/common/components/Punchup/primitives/display/PunchupTile';
import { PunchupPoolTile } from 'features/common/components/Punchup/composites/tiles/PunchupPoolTile';
import { PunchupHelpButton } from 'features/common/components/Punchup/composites/content/PunchupHelpButton';
import { PunchupDivider } from 'features/common/components/Punchup/primitives/layout/PunchupDivider';
import { PunchupInlineEditor } from 'features/common/components/Punchup/composites/editors/PunchupInlineEditor';
import { PunchupMultiButtonSectionTitle } from 'features/common/components/Punchup/primitives/layout/PunchupMultiButtonSectionTitle';
import { PunchupSpinner } from 'features/common/components/Punchup/primitives/feedback/PunchupSpinner';
import { ROUTES } from 'features/routes';
import { useResponsiveScreen } from 'hooks/useWindowSize';
import { useTranslation } from 'react-i18next';
import { useEventCapacity } from 'features/common/hooks/useEventCapacity';
import { useTicketTypeColors } from 'features/common/hooks/useTicketTypeColors';
import { POOL_OVERSUBSCRIBED_MESSAGE } from 'features/common/utils/poolCopy';

interface PoolsSectionProps {
	/**
	 * When true the section is rendered inside a PunchupAccordionSection.
	 * Add/Edit are handled inline (no sub-page navigation).
	 */
	inAccordion?: boolean;
}

const PoolsSection: FC<PoolsSectionProps> = ({ inAccordion = false }) => {
	const { t } = useTranslation();
	const { id }: IdParamsType = useParams();
	const isLargeScreen = useResponsiveScreen();
	const { push } = useHistory();

	// `used` is the Lifetime-Σ value from useEventCapacity (shared with the donut + Send tile), not the
	// stale two-writer pool.sold scalar — see PoolsSection/CLAUDE.md "Tile rendering" + pools_business_logic.md §1.
	const { pools, isLoading } = useEventCapacity();
	// Tint each member chip with its ticket type's colour (resolved by name from the shared source).
	const { resolveByName } = useTicketTypeColors();

	const onClickAddNew = () => {
		push(generatePath(ROUTES.POOLS_NEW_PATH, { id }));
	};

	const onClickEdit = (poolId: string) => push(generatePath(ROUTES.POOLS_EDIT_PATH, { id, poolId }));

	const renderTiles = (onTileClick: (poolId: string) => void) => (
		<PunchupCardTileGrid>
			{pools.length > 0 ? (
				pools.map((pool) => {
					// Lifetime-Σ "used" headline — the reciprocal of the Send Tickets tile's "available"
					// (used + available = total). A legacy pool can read over capacity (used > total) honestly;
					// the shared PunchupPoolTile flags it with a warning icon + over-capacity tooltip rather
					// than clamping, matching the over-subscribed red center on the analytics pool donut.
					const overCapacity = pool.sold > pool.totalAmount;
					return (
						<PunchupPoolTile
							key={pool.poolId}
							name={pool.name}
							headline={`${pool.sold} / ${pool.totalAmount} ${t('poolsSection-used', 'used')}`}
							memberNames={pool.memberNames}
							memberColors={pool.memberNames.map((member) => resolveByName(member))}
							overCapacity={overCapacity}
							descriptions={
								// The pool description is intentionally NOT shown in the list view — it's edited
								// on the pool form. Keep only the empty-state hint when a pool has no ticket types.
								pool.memberNames.length === 0
									? [t('poolsSection-no-ticket-types', 'No ticket types assigned')]
									: undefined
							}
							helpButton={
								<PunchupHelpButton
									mainText={t(
										'poolsSection-tile-help',
										'A shared pool — its ticket types draw from one shared capacity.'
									)}
									codeText={`shared capacity = ${pool.totalAmount}\nused (sold + comped) = ${pool.sold}\nleft = ${pool.poolRemaining}`}
									secondaryText={overCapacity ? POOL_OVERSUBSCRIBED_MESSAGE : undefined}
								/>
							}
							onClick={() => onTileClick(pool.poolId.toString())}
							dataCy={`pool-tile-${pool.poolId}`}
						/>
					);
				})
			) : (
				<p className="punchupEmptyState">{t('poolsSection-empty', 'This event has no pools')}</p>
			)}
		</PunchupCardTileGrid>
	);

	// ── Accordion (inline add/edit) mode ────────────────────────────

	if (inAccordion) {
		// One form for every pool — create and edit alike. `PoolForm` handles both via its
		// `poolId` prop (null → create). Pools are non-seated allocation buckets that may hold
		// several ticket types under a shared capacity; the section itself is not rendered for
		// seated events (gated at PunchupDashboard via `!seatsEventKey`).
		return (
			<PunchupInlineEditor
				addLabel={t('dashboard-add-green')}
				extraViewActions={isLoading ? <PunchupSpinner size={16} /> : undefined}
				renderView={({ startEdit }) => renderTiles(startEdit)}
				renderEdit={({ editingId, done }) => <PoolForm poolId={editingId} onDone={done} />}
			/>
		);
	}

	// ── Standalone mode ─────────────────────────────────────────────

	return (
		<>
			<PunchupMultiButtonSectionTitle
				title={t('dashboard-pools-green')}
				buttons={[{ label: t('dashboard-add-green'), onClick: onClickAddNew, isAdd: true }]}
				inAccordion={false}
			/>

			{renderTiles(onClickEdit)}

			{!isLargeScreen && <PunchupDivider />}
		</>
	);
};

export { PoolsSection };
